<template>
    <div v-if="previewLines" class="min-w-0 w-full" data-admin2026-expandable-text>
        <p ref="preview" :id="textId || null" :class="[textClasses, {'line-clamp-2': !expanded && previewLines === 2, 'line-clamp-3': !expanded && previewLines !== 2}]"><slot>{{ plainText }}</slot></p>
        <button v-if="overflowing || expanded" type="button" class="mt-1 cursor-pointer text-xs font-medium text-pprimary600 hover:underline" :aria-expanded="expanded" :aria-controls="textId || null" @click="expanded = !expanded" data-admin2026-expandable-text-toggle>{{ expanded ? 'Show less' : 'Show more' }}</button>
    </div>
    <details v-else-if="hasMoreText"
             class="group min-w-0"
             data-admin2026-training-plan-quick-preview-expandable-note="true">
        <summary :class="['flex cursor-pointer list-none items-baseline gap-1.5', textClasses]">
            <span class="min-w-0 truncate" v-text="summaryText"></span>
            <span class="shrink-0 font-semibold text-emerald-700 group-open:hidden dark:text-emerald-300"
                  data-admin2026-training-plan-quick-preview-note-toggle="expand">Expand</span>
            <span class="hidden shrink-0 font-semibold text-emerald-700 group-open:inline dark:text-emerald-300"
                  data-admin2026-training-plan-quick-preview-note-toggle="collapse">Collapse</span>
        </summary>
        <p :class="['mt-1 whitespace-pre-line', textClasses]" v-text="plainText"></p>
    </details>
    <p v-else :class="['truncate', textClasses]" v-text="plainText"></p>
</template>

<script>
export default {
    name: 'TrainingPlanQuickPreviewNote',
    props: {
        value: {
            type: [String, Number],
            default: '',
        },
        maximumLength: {
            type: Number,
            default: 120,
        },
        textClasses: {
            type: String,
            default: 'text-xs leading-4 text-slate-600 dark:text-slate-300',
        },
        previewLines: {type: Number, default: 0},
        textId: {type: String, default: ''},
    },
    data() { return {expanded: false, overflowing: false}; },
    mounted()
    {
        if (!this.previewLines) return;
        this.measurePreview();
        this.previewObserver = new ResizeObserver(() => this.measurePreview());
        this.previewObserver.observe(this.$refs.preview);
    },
    updated() { if (this.previewLines) this.measurePreview(); },
    beforeUnmount() { this.previewObserver?.disconnect(); },
    methods: {
        measurePreview()
        {
            if (this.expanded || !this.$refs.preview) return;
            this.overflowing = this.$refs.preview.scrollHeight > this.$refs.preview.clientHeight + 1;
        },
    },
    computed: {
        plainText()
        {
            if (this.value === null || typeof this.value === 'undefined')
            {
                return '';
            }

            const withLineBreaks = String(this.value)
                .replace(/<br\s*\/?>/gi, '\n')
                .replace(/<\/(p|div|li|h[1-6])>/gi, '\n');
            const container = document.createElement('div');
            container.innerHTML = withLineBreaks;

            return (container.textContent || container.innerText || '')
                .replace(/\u00a0/g, ' ')
                .split(/\r?\n/)
                .map(line => line.replace(/\s+/g, ' ').trim())
                .filter(line => line !== '')
                .join('\n');
        },
        summaryText()
        {
            const firstLine = (this.plainText.split(/\r?\n/)[0] || '').trim();
            const limit = Math.max(parseInt(this.maximumLength || 120, 10), 20);

            return firstLine.length > limit
                ? firstLine.substring(0, limit - 1).trimEnd() + '…'
                : firstLine;
        },
        hasMoreText()
        {
            return this.plainText !== '' && this.plainText !== this.summaryText;
        },
    },
};
</script>
